import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

/**
 * Homepage promo configuration is now sourced from the backend
 * (`GET /api/site-settings/homepage-promo`) so admins can flip a coupon
 * "Promote on homepage" toggle in `/admin/coupons` without a code
 * deploy. The legacy static `HOMEPAGE_PROMO` / `isPromoActive()` exports
 * are kept as fallback values for first paint before the fetch resolves.
 *
 * Components should prefer the `usePromo()` hook below — it returns
 * `{ promo, isPromoActive, refresh }` and re-renders on state changes.
 */

const DEFAULT_PROMO = {
  enabled: false,
  expiresAt: null,
  dismissKey: 'home_promo_dismissed_v1',
  badge: '',
  headline: '',
  code: '',
  bodySuffix: '',
  endsLabel: '',
  ctaPath: '/pricing',
  redeemPath: '/subscription',
  redeemLabel: 'Redeem here',
};

const PromoContext = createContext({
  promo: DEFAULT_PROMO,
  isPromoActive: false,
  loading: true,
  refresh: () => Promise.resolve(),
});

export function PromoProvider({ children }) {
  const [promo, setPromo] = useState(DEFAULT_PROMO);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/site-settings/homepage-promo');
      if (data && data.enabled) {
        setPromo({ ...DEFAULT_PROMO, ...data });
      } else {
        setPromo({ ...DEFAULT_PROMO, enabled: false });
      }
    } catch {
      setPromo({ ...DEFAULT_PROMO, enabled: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Derived `isPromoActive` — true when enabled AND not past expiry.
  const isPromoActive = !!promo.enabled && (
    !promo.expiresAt || Date.now() < new Date(promo.expiresAt).getTime()
  );

  return (
    <PromoContext.Provider value={{ promo, isPromoActive, loading, refresh }}>
      {children}
    </PromoContext.Provider>
  );
}

/** Hook — returns the live promo config + helpers. */
export function usePromo() {
  return useContext(PromoContext);
}

// ── Legacy compatibility exports ──────────────────────────────────────
// Callers that import HOMEPAGE_PROMO directly will see the default
// (inert) shape. Once they're migrated to `usePromo()` these can be
// removed. New code should use the hook.
export const HOMEPAGE_PROMO = DEFAULT_PROMO;

/**
 * Standalone check used by a few legacy callsites. Returns false until
 * components migrate to `usePromo()`. The hook is the source of truth.
 */
export function isPromoActive() {
  return false;
}
